import { LabelBar } from '@/app/platform/core/layout/themes/components/LabelBar.tsx';
import { useLabelFilter } from '@/app/platform/core/layout/themes/components/useLabelFilter.ts';
import { useTheme } from '@/app/platform/core/layout/themes/ThemeContext.tsx';
import { useRegisterTabActions } from '@/app/platform/core/layout/useRegisterTabActions.ts';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { User } from 'lucide-react';

import AddColumnForm from './AddColumnForm.tsx';
import KanbanCardModal from './KanbanCardModal.tsx';
import KanbanColumnComponent from './KanbanColumn.tsx';
import { useKanban } from './hooks.ts';

interface Props {
  featureInstanceId: string;
  canEdit: boolean;
  isManager: boolean;
}

const KanbanTab: React.FC<Props> = ({
  featureInstanceId,
  canEdit,
  isManager,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const {
    board,
    persons,
    error,
    loaded,
    createColumn,
    renameColumn,
    deleteColumn,
    moveColumn,
    createCard,
    updateCard,
    archiveCard,
    restoreCard,
    moveCard,
    reorderCard,
    createLabel,
    updateLabel,
    deleteLabel,
    reorderLabels,
    toggleCardLabel,
    setCardReminders,
  } = useKanban(featureInstanceId);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlTaskId = searchParams.get('taskId');
  const urlHighlight = searchParams.get('q') ?? undefined;

  const [configuring, setConfiguring] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [filterPersonId, setFilterPersonId] = useState<string | null>(null);

  const deepLinkedCard = urlTaskId ? board.cards.find((c) => c.id === urlTaskId) ?? null : null;

  const closeDeepLinked = () => {
    searchParams.delete('taskId');
    searchParams.delete('q');
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const hasArchived = board.cards.some((c) => c.status === 'archived');

  const tabActions = useMemo(
    () => [
      ...(isManager
        ? [
            {
              id: 'kanban.configureColumns',
              icon: 'settings' as const,
              badgeIcon: 'layers' as const,
              label: configuring
                ? t('features.kanban.saveColumns')
                : t('features.kanban.configureColumns'),
              onClick: () => setConfiguring((v) => !v),
            },
          ]
        : []),
      ...(canEdit && hasArchived
        ? [
            {
              id: 'kanban.toggleArchived',
              icon: (showArchived ? 'eye-off' as const : 'eye' as const),
              label: showArchived
                ? t('features.kanban.hideArchived')
                : t('features.kanban.showArchived'),
              onClick: () => setShowArchived((v) => !v),
              variant: 'danger' as const,
            },
          ]
        : []),
      ...(urlHighlight
        ? [
            {
              id: 'kanban.removeSearchHighlight',
              icon: 'x' as const,
              badgeIcon: 'layers' as const,
              label: t('search.removeHighlight'),
              onClick: () => {
                const next = new URLSearchParams(searchParams);
                next.delete('q');
                navigate({ search: next.toString() }, { replace: true });
              },
            },
          ]
        : []),
    ],
    [isManager, canEdit, hasArchived, configuring, showArchived, t, urlHighlight, searchParams, navigate],
  );

  useRegisterTabActions(tabActions);

  const initDone = useRef(false);
  const sortedCols = [...board.columns].sort((a, b) => a.position - b.position);
  const maxColumns = 4;

  const activeCardLabelIds = new Set(
    board.cards
      .filter((c) => c.status === 'active')
      .flatMap((c) => c.label_ids),
  );
  const { filterLabelIds, toggleFilterLabel } = useLabelFilter(activeCardLabelIds);
  const labelUsageCounts: Record<string, number> = {};
  board.cards
    .filter((c) => c.status === 'active')
    .forEach((c) => c.label_ids.forEach((id) => {
      labelUsageCounts[id] = (labelUsageCounts[id] ?? 0) + 1;
    }));
  const assignedPersons = persons.filter((p) =>
    board.cards.some(
      (c) => c.status === 'active' && c.assigned_person_id === p.id,
    ),
  );

  useEffect(() => {
    if (!loaded || initDone.current || !isManager) return;
    initDone.current = true;
    if (board.columns.length === 0) setConfiguring(true);
  }, [loaded, board.columns.length, isManager]);

  return (
    <div>
      {error && (
        <div
          style={{
            padding: '8px 12px',
            marginBottom: '12px',
            color: theme.colors.danger,
            fontSize: 'var(--font-sm)',
          }}
        >
          {error}
        </div>
      )}

      {/* Filters bar */}
      <div style={{ marginBottom: '10px' }}>
        <LabelBar
          labels={board.labels}
          activeLabelIds={activeCardLabelIds}
          filterLabelIds={filterLabelIds}
          onFilter={toggleFilterLabel}
          canEditLabels={isManager && configuring}
          usageCounts={labelUsageCounts}
          onCreate={async (name, color) => {
            await createLabel({ feature_instance_id: featureInstanceId, name, color });
          }}
          onUpdate={updateLabel}
          onDelete={deleteLabel}
          onReorder={reorderLabels}
        />
        {assignedPersons.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center',
              marginTop: '8px',
            }}
          >
            <User size={14} color={theme.colors.primary} />
            {assignedPersons.map((person) => {
              const active = filterPersonId === person.id;
              return (
                <button
                  key={person.id}
                  type="button"
                  onClick={() =>
                    setFilterPersonId((prev) =>
                      prev === person.id ? null : person.id,
                    )
                  }
                  style={{
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: 'var(--font-xs)',
                    fontWeight: active ? 700 : 500,
                    cursor: 'pointer',
                    border: `1px solid ${theme.colors.primary}`,
                    backgroundColor: active
                      ? theme.colors.primary
                      : 'transparent',
                    color: active
                      ? theme.colors.surface
                      : theme.colors.primary,
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {person.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Columns */}
      <div
        style={{
          display: 'flex',
          gap: '14px',
          alignItems: 'flex-start',
          overflowX: 'auto',
        }}
      >
        {sortedCols.map((col, idx) => (
          <KanbanColumnComponent
            key={col.id}
            column={col}
            board={board}
            featureInstanceId={featureInstanceId}
            canEdit={canEdit}
            configuring={isManager && configuring}
            isFirst={idx === 0}
            isLast={idx === sortedCols.length - 1}
            canDelete={
              sortedCols.length > 2 &&
              idx !== 0 &&
              idx !== sortedCols.length - 1
            }
            showArchived={showArchived}
            filterLabelIds={filterLabelIds}
            filterPersonId={filterPersonId}
            persons={persons}
            onRename={renameColumn}
            onMove={moveColumn}
            onDelete={deleteColumn}
            onCreateCard={createCard}
            onUpdateCard={updateCard}
            onArchiveCard={archiveCard}
            onRestoreCard={restoreCard}
            onMoveCard={moveCard}
            onReorderCard={reorderCard}
            onToggleLabel={toggleCardLabel}
            onSetReminders={setCardReminders}
            onCreateLabel={createLabel}
            onUpdateLabel={updateLabel}
            onDeleteLabel={deleteLabel}
            onReorderLabel={reorderLabels}
          />
        ))}

        {/* Add column */}
        {isManager && configuring && sortedCols.length < maxColumns && (
          <div style={{ minWidth: '200px', flex: '0 0 auto' }}>
            <AddColumnForm
              onAdd={(name) =>
                createColumn({ feature_instance_id: featureInstanceId, name })
              }
            />
          </div>
        )}
      </div>

      {deepLinkedCard && (
        <KanbanCardModal
          card={deepLinkedCard}
          boardLabels={board.labels}
          persons={persons}
          canEdit={canEdit}
          highlightToken={urlHighlight}
          onClose={closeDeepLinked}
          onUpdate={updateCard}
          onToggleLabel={toggleCardLabel}
          onSetReminders={setCardReminders}
          onCreateLabel={createLabel}
          onUpdateLabel={updateLabel}
          onDeleteLabel={deleteLabel}
          onReorderLabel={reorderLabels}
        />
      )}
    </div>
  );
};

export default KanbanTab;
